import { useCallback, useState } from "react";
import { ColumnInstance } from "react-table";
import { genericMemo } from "../../../../../utils";
import { Drawer } from "../../../Drawer";
import { Link } from "../../../Link";
import { SortableList } from "../../../SortableList";
import { BaseColumnTypeAdditionalProps } from "../../TableContainer";
import SortingContext from "../Table/contexts/SortingContext";
import VisibleColumnsContext from "../Table/contexts/VisibleColumnsContext";
import ColumnItem from "./ColumnItem";

import { ReactComponent as SettingsIcon } from "./icons/settings.svg";

type Created<T extends object> = ColumnInstance<T> & BaseColumnTypeAdditionalProps<T>;

type SettingsProps<T extends object> = {
  resetViewSettings: () => void,
  columns: Created<T>[],
  columnOrderChange: (items: Created<T>[]) => void
}

const Settings = genericMemo(<T extends object>({ resetViewSettings, columns, columnOrderChange }: SettingsProps<T>) => {
  const [ isOpen, setIsOpen ] = useState(false);
  const onRequestClose = useCallback(() => setIsOpen(false), [ setIsOpen ]);
  return (
    <>
      <Link
        component="button"
        type="button"
        linkType="s"
        onClick={() => setIsOpen(true)}>
        <SettingsIcon width={15} height={15} />Settings
      </Link>
      <Drawer isOpen={isOpen} onRequestClose={onRequestClose} title={"Settings"}>
        <Link
          style={{ marginBottom: 15 }}
          component="button"
          linkType="m"
          type="button"
          onClick={resetViewSettings}
        >
          Reset to default settings
        </Link>
        <SortableList
          renderClone
          value={columns}
          onChange={columnOrderChange}
          getItemId={item => item.id}
          filterValues={v => !v.hideInSettings}
          renderItem={
            (column) =>
              (
                <SortingContext.Consumer>
                  {() =>
                    <VisibleColumnsContext.Consumer>
                      {() =>
                        <ColumnItem
                          key={`item-${column.id}`}
                          name={column.Header as any}
                          isSorted={column.isSorted}
                          sorting={column.isSortedDesc}
                          visible={column.isVisible}
                          disabledSortBy={column.disableSortBy}
                          toggleSorting={column.getSortByToggleProps()?.onClick as any}
                          toggleColumnVisibility={column.getToggleHiddenProps()}
                        />
                      }
                    </VisibleColumnsContext.Consumer>
                  }
                </SortingContext.Consumer>
              )
          } />
      </Drawer>
    </>
  );
});

export default Settings;
